import type { OptionsMap } from '@openx/types/options.ts';
import { type DisplayContextsType, displayContexts } from '@openx/types/targeting/targeting.ts';
import { TechnologyAndDevicesOption, type UserAgentPayload } from '@openx/types/targeting/technologyAndDevices.ts';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions.ts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { OptionsDropdown } from '../../shared/OptionsDropdown/OptionsDropdown.tsx';
import { optionsToFetch } from '../constants.ts';
import { sortBrowserOptionsKeys, transformOptionsToMatchLegacyApi } from '../utils.ts';
import { MobileCarrierDropdown } from './MobileCarrierDropdown.tsx';
import { SingleSelectDropdown } from './SingleSelectDropdown.tsx';
import { UserAgentDropdown } from './UserAgentDropdown.tsx';

type TopBoxProps = {
  items: Set<string> | UserAgentPayload;
  onChange: (options: Set<string> | UserAgentPayload) => void;
  selectedType: TechnologyAndDevicesOption;
  displayContext?: DisplayContextsType;
};

export const TopBoxValueField = ({ items, onChange, selectedType, displayContext }: TopBoxProps) => {
  const { t } = useTranslation();
  const { useTechnologyOptionsFetch } = useTargetingContext();

  const shouldFetchOptions = !!optionsToFetch[selectedType];
  const { options, isLoading } = useTechnologyOptionsFetch(selectedType, shouldFetchOptions);
  let transformedOptions: OptionsMap = {};

  if (selectedType === TechnologyAndDevicesOption.SCREEN_RESOLUTION) {
    transformedOptions = transformOptionsToMatchLegacyApi(options);
  }

  const optionsToDisplay = Object.keys(transformedOptions).length ? transformedOptions : options;

  const placeholders = useMemo(
    () => ({
      [TechnologyAndDevicesOption.DEVICE_TYPE]:
        displayContext === displayContexts.TRAFFIC_SET ? t('[ Select Device Types ]') : t('[ Select Devices ]'),
      [TechnologyAndDevicesOption.OS]: t('[ Select an Operating System ]'),
      [TechnologyAndDevicesOption.BROWSER]: t('[ Select a Browser ]'),
      [TechnologyAndDevicesOption.LANGUAGE]: t('[ Select a Language ]'),
      [TechnologyAndDevicesOption.API_FRAMEWORK]: t('[ Select an API Frameworks ]'),
      [TechnologyAndDevicesOption.CONNECTION_SPEED]: t('[ Select a Connection Speed ]'),
      [TechnologyAndDevicesOption.CONNECTION_TYPE]: t('[ Select a Connection Type ]'),
      [TechnologyAndDevicesOption.DEVICE]: t('[ Select a Device ]'),
      [TechnologyAndDevicesOption.DEVICE_ID]: t('[ Select a Device ID ]'),
      [TechnologyAndDevicesOption.HTTPS]: t('[ Select a HTTPS ]'),
      [TechnologyAndDevicesOption.MOBILE_CARRIER]: t('[ Select a Mobile Carrier ]'),
      [TechnologyAndDevicesOption.MKT_COOKIES]: t('[ Select a Cookies ]'),
      [TechnologyAndDevicesOption.SCREEN_RESOLUTION]: t('[ Select a Screen Resolution ]'),
      [TechnologyAndDevicesOption.USER_AGENT]: t('[ Select an User Agent ]'),
    }),
    [t, displayContext]
  );

  const labels = useMemo(
    () => ({
      [TechnologyAndDevicesOption.API_FRAMEWORK]: t('API Framework'),
      [TechnologyAndDevicesOption.BROWSER]: t('Browser'),
      [TechnologyAndDevicesOption.LANGUAGE]: t('Language'),
      [TechnologyAndDevicesOption.OS]: t('OS'),
    }),
    [t]
  );

  const placeholder = placeholders[selectedType];
  const label = labels[selectedType];

  const getSortedKeys = useCallback(
    options => {
      if (selectedType !== TechnologyAndDevicesOption.BROWSER) {
        return produceSortedOptionsKeys(options);
      }
      return sortBrowserOptionsKeys(options);
    },
    [selectedType]
  );

  if (items instanceof Set) {
    if (selectedType === TechnologyAndDevicesOption.MOBILE_CARRIER) {
      return (
        <MobileCarrierDropdown
          loading={isLoading}
          options={options}
          onChange={onChange}
          selectedOptions={items}
          placeholder={placeholder}
          selectedType={selectedType}
        />
      );
    }

    if (
      selectedType === TechnologyAndDevicesOption.MKT_COOKIES ||
      selectedType === TechnologyAndDevicesOption.DEVICE_ID ||
      selectedType === TechnologyAndDevicesOption.HTTPS
    ) {
      return <SingleSelectDropdown onChange={onChange} selectedType={selectedType} />;
    }

    return (
      <OptionsDropdown
        loading={isLoading}
        options={optionsToDisplay}
        onChange={values => onChange(values)}
        selectedOptions={items}
        placeholder={placeholder}
        getSortedKeys={getSortedKeys}
        dataTest="technology-input"
        label={label}
      />
    );
  }

  return <UserAgentDropdown onChange={onChange} selectedOptions={items} />;
};
